import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import api from "../../../config/URL";
import PhoneInput from "react-phone-input-2";

const validationSchema = Yup.object().shape({
  emergencyContactNo: Yup.string()
    .matches(/^\+\d{10,15}$/, "*Invalid Phone Number")
    .notRequired(),
  emergencyContactInformation: Yup.array().of(
    Yup.object().shape({
      contactNo: Yup.string()
        .matches(/^\+\d{10,15}$/, "*Invalid Phone Number")
        .notRequired(),
      postalCode: Yup.string()
        .matches(/^\d{6}$/, "Invalid Number(6 digit)")
        .notRequired(),
      files: Yup.mixed()
        .notRequired()
        .test(
          "max-file-name-length",
          "*File name must be at most 50 characters",
          (value) => !value || (value.name && value.name.length <= 50)
        ),
    })
  ),
});

const AddEmergencyContact = forwardRef(
  ({ formData, setLoadIndicators, setFormData, handleNext }, ref) => {
    const userName = localStorage.getItem("userName");
    const [profileImage, setProfileImage] = useState([]);
    const [rows, setRows] = useState([]);
    const [leadDataTrue, setLeadDataTrue] = useState(true);

    // Initialize Formik with dynamic initial values based on formData
    const formik = useFormik({
      initialValues: {
        emergencyContactId: formData.emergencyContactId || "",
        emergencyContactName: formData.emergencyContactName || "",
        emergencyRelation: formData.emergencyRelation || "",
        emergencyContactNo: formData.emergencyContactNo || "",
        otherRelation: formData.otherRelation || "",
        emergencyContactInformation: formData.emergencyContactInformation || [
          {
            name: "",
            authorizedRelation: "",
            contactNo: "",
            postalCode: "",
            emergencyContactAddress: "",
            files: null,
          },
        ],
      },
      validationSchema: validationSchema,
      onSubmit: async (data) => {
        setLoadIndicators(true);
        try {
          const formDatas = new FormData();
          formDatas.append("emergencyContactName", data.emergencyContactName || "");
          formDatas.append("emergencyRelation", data.emergencyRelation || "");
          formDatas.append("emergencyContactNo", data.emergencyContactNo || "");
          formDatas.append("otherRelation", data.otherRelation || "");
          data.emergencyContactInformation?.forEach((contact, index) => {
            formDatas.append(`name[${index}]`, contact.name || "");
            formDatas.append(`contactNo[${index}]`, contact.contactNo || "");
            formDatas.append(`authorizedRelation[${index}]`, contact.authorizedRelation || "");
            formDatas.append(`postalCode[${index}]`, contact.postalCode || "");
            formDatas.append(`emergencyContactAddress[${index}]`, contact.emergencyContactAddress || "");
            if (contact.files && !String(contact.files).startsWith("http")) {
              formDatas.append(`files[${index}]`, contact.files || "");
            }
            if (contact.id) {
              formDatas.append(`emergencyAuthorizedContactIds[${index}]`, contact.id);
            }
          });

          let response;
          if (data.emergencyContactId) {
            response = await api.put(
              `/updateEmergencyContactWithEmergencyAuthorizedContact/${data.emergencyContactId}`,
              formDatas,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          } else {
            response = await api.post(
              `/createEmergencyContactWithEmergencyAuthorizedContact/${formData.student_id}`,
              formDatas,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          }

          if (response.status === 200 || response.status === 201) {
            toast.success(response.data.message);
            setFormData({ ...formData, ...data }); // Update formData with submitted data
            handleNext();
            setLeadDataTrue(false);
          } else {
            toast.error(response.data.message);
          }
        } catch (error) {
          toast.error(error.message || "An error occurred");
        } finally {
          setLoadIndicators(false);
        }
      },
    });

    // Fetch lead data if LeadId exists and leadDataTrue is true
    const getLeadData = async () => {
      if (formData.LeadId && leadDataTrue) {
        try {
          const response = await api.get(`/getAllLeadInfoById/${formData.LeadId}`);
          const leadData = response.data;
          const emergencyContactInfo = [
            {
              name: leadData.nameOfAuthorised || "",
              authorizedRelation: leadData.relationToChils || "",
              contactNo: leadData.contactOfAuthorised || "",
              postalCode: leadData.postalCode || "",
              emergencyContactAddress: leadData.address || "",
              files: null,
            },
          ];

          formik.setValues({
            emergencyContactName: leadData.nameOfEmergency || "",
            emergencyContactNo: leadData.emergencyContact || "",
            emergencyRelation: leadData.nameOfEmergency || "",
            otherRelation: "",
            emergencyContactInformation: emergencyContactInfo,
          });
          setRows([{}]);
          setFormData({
            ...formData,
            emergencyContactName: leadData.nameOfEmergency || "",
            emergencyContactNo: leadData.emergencyContact || "",
            emergencyRelation: leadData.nameOfEmergency || "",
            emergencyContactInformation: emergencyContactInfo,
          });
        } catch (error) {
          console.error("Error fetching lead data:", error);
          toast.error("Error fetching lead data");
        }
      }
    };

    // Fetch student data if student_id exists
    const getStudentData = async () => {
      try {
        const response = await api.get(`/getAllStudentById/${formData.student_id}`);
        const studentData = response.data;

        if (studentData.studentEmergencyContacts?.length > 0) {
          const contactData = studentData.studentEmergencyContacts[0];
          const emergencyContactInfo = contactData.emergencyAuthorizedContactModels?.map((item) => ({
            id: item.id,
            name: item.name || "",
            authorizedRelation: item.authorizedRelation || "",
            contactNo: item.contactNo || "",
            postalCode: item.postalCode || "",
            emergencyContactAddress: item.emergencyContactAddress || "",
            files: item.personProfile || null,
          })) || [
            {
              name: "",
              authorizedRelation: "",
              contactNo: "",
              postalCode: "",
              emergencyContactAddress: "",
              files: null,
            },
          ];

          formik.setValues({
            emergencyContactId: contactData.id || "",
            emergencyContactName: contactData.emergencyContactName || "",
            emergencyRelation: contactData.emergencyRelation || "",
            emergencyContactNo: contactData.emergencyContactNo || "",
            otherRelation: contactData.otherRelation || "",
            emergencyContactInformation: emergencyContactInfo,
          });

          setRows(emergencyContactInfo.map(() => ({})));
          setProfileImage(contactData.emergencyAuthorizedContactModels?.map((item) => item.personProfile) || []);
          setFormData({
            ...formData,
            emergencyContactId: contactData.id || "",
            emergencyContactName: contactData.emergencyContactName || "",
            emergencyRelation: contactData.emergencyRelation || "",
            emergencyContactNo: contactData.emergencyContactNo || "",
            otherRelation: contactData.otherRelation || "",
            emergencyContactInformation: emergencyContactInfo,
          });
        } else {
          const defaultEmergencyContactInfo = [
            {
              name: "",
              authorizedRelation: "",
              contactNo: "",
              postalCode: "",
              emergencyContactAddress: "",
              files: null,
            },
          ];
          formik.setValues({
            emergencyContactId: "",
            emergencyContactName: "",
            emergencyRelation: "",
            emergencyContactNo: "",
            otherRelation: "",
            emergencyContactInformation: defaultEmergencyContactInfo,
          });
          setRows([{}]);
          setProfileImage([]);
        }
      } catch (error) {
        console.error("Error fetching student data:", error);
        toast.error("Error fetching student data");
      }
    };

    // Initialize form state based on formData or fetch data
    useEffect(() => {
      if (formData.LeadId && leadDataTrue) {
        getLeadData();
      } else {
        getStudentData();
      }
    }, [formData.LeadId, leadDataTrue]);

    // Update rows when emergencyContactInformation changes
    useEffect(() => {
      setRows(formik.values.emergencyContactInformation.map(() => ({})));
    }, [formik.values.emergencyContactInformation]);

    // Handle leadDataTrue based on emergencyContactInformation
    useEffect(() => {
      if (
        formData.emergencyContactInformation &&
        formData.emergencyContactInformation.length > 0 &&
        formData.emergencyContactInformation[0]?.emergencyAuthorizedContactModels?.length > 0
      ) {
        setLeadDataTrue(false);
      }
    }, [formData.emergencyContactInformation]);

    const handleRemoveRow = (index) => {
      const updatedEmergencyInformation = [...formik.values.emergencyContactInformation];
      updatedEmergencyInformation.splice(index, 1);
      formik.setFieldValue("emergencyContactInformation", updatedEmergencyInformation);
      setRows((prev) => {
        const newRows = [...prev];
        newRows.splice(index, 1);
        return newRows;
      });
      setProfileImage((prev) => {
        const newImages = [...prev];
        newImages.splice(index, 1);
        return newImages;
      });
    };

    const handleDeleteRow = async (index) => {
      const contactId = formik.values.emergencyContactInformation[index]?.id;
      if (contactId) {
        try {
          const response = await api.delete(`/deleteEmergencyAuthorizedContact/${contactId}`);
          if (response.status === 200 || response.status === 201) {
            toast.success("Contact deleted successfully");
            handleRemoveRow(index);
            getStudentData();
          }
        } catch (error) {
          toast.error("Failed to delete the contact");
          console.error(error);
        }
      } else {
        handleRemoveRow(index);
      }
    };

    useImperativeHandle(ref, () => ({
      EmergencyContact: formik.handleSubmit,
    }));

    useEffect(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, []);

    return (
      <div className="container-fluid p-2">
        <form
          onSubmit={formik.handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !formik.isSubmitting) {
              e.preventDefault();
            }
          }}
        >
          <div className="border-0 mb-5">
            <p className="headColor">Emergency Contact</p>
            <div className="container py-3">
              <div className="row mt-3">
                <div className="col-lg-6 col-md-6 col-12">
                  <div className="text-start mt-4">
                    <label className="mb-1 fw-medium">
                      <small>Emergency Contact Name</small>
                    </label>
                    <br />
                    <input
                      className="form-control"
                      type="text"
                      name="emergencyContactName"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.emergencyContactName}
                    />
                    {formik.touched.emergencyContactName && formik.errors.emergencyContactName && (
                      <div className="text-danger">
                        <small>{formik.errors.emergencyContactName}</small>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-12">
                  <div className="text-start mt-4">
                    <label className="mb-1 fw-medium">
                      <small>Emergency Contact No</small>
                    </label>
                    <br />
                    <PhoneInput
                      country={"sg"}
                      enableSearch={true}
                      inputProps={{
                        name: "emergencyContactNo",
                        onBlur: formik.handleBlur,
                      }}
                      value={formik.values.emergencyContactNo}
                      onChange={(phone) => formik.setFieldValue("emergencyContactNo", "+" + phone)}
                      containerClass="phone-input-container"
                      inputClass="phone-input"
                      buttonClass="phone-flag-button"
                    />
                    {formik.touched.emergencyContactNo && formik.errors.emergencyContactNo && (
                      <div className="text-danger">
                        <small>{formik.errors.emergencyContactNo}</small>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 col-12">
                  <div className="text-start mt-4">
                    <label className="mb-1 fw-medium">
                      <small>Emergency Contact Relation</small>
                    </label>
                    <br />
                    <select
                      className="form-select"
                      name="emergencyRelation"
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        formik.setFieldValue("emergencyRelation", selectedValue);
                        if (selectedValue !== "Others") {
                          formik.setFieldValue("otherRelation", "");
                        }
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.emergencyRelation}
                    >
                      <option value="">Select Relation</option>
                      <option value="Helper">Helper</option>
                      <option value="Legal Guardian">Legal Guardian</option>
                      <option value="Aunt">Aunt</option>
                      <option value="Uncle">Uncle</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Grandfather">Grandfather</option>
                      <option value="Grandmother">Grandmother</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                </div>
                {formik.values.emergencyRelation === "Others" && (
                  <div className="col-lg-6 col-md-6 col-12">
                    <div className="text-start mt-4">
                      <label className="mb-1 fw-medium">
                        <small>Other Relation Description</small>
                      </label>
                      <br />
                      <textarea
                        className="form-control"
                        rows={5}
                        name="otherRelation"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.otherRelation}
                      />
                      {formik.touched.otherRelation && formik.errors.otherRelation && (
                        <div className="text-danger">
                          <small>{formik.errors.otherRelation}</small>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="border-0 mb-5">
            {formik.values.emergencyContactInformation.map((row, index) => (
              <div className="border-0 mb-5" key={index}>
                <div className="border-0 my-2">
                  <p className="headColor">
                    Authorized Person to Take Child from Class #{index + 1}
                  </p>
                  <div className="container py-3">
                    <div className="row mt-3">
                      <div className="col-lg-6 col-md-6 col-12">
                        <div className="text-start mt-4">
                          <label className="mb-1 fw-medium">
                            <small>Name</small>
                          </label>
                          <br />
                          <input
                            className="form-control"
                            type="text"
                            name={`emergencyContactInformation[${index}].name`}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.emergencyContactInformation[index].name || ""}
                          />
                          {formik.touched.emergencyContactInformation?.[index]?.name &&
                            formik.errors.emergencyContactInformation?.[index]?.name && (
                              <div className="text-danger">
                                <small>
                                  {formik.errors.emergencyContactInformation[index].name}
                                </small>
                              </div>
                            )}
                        </div>
                        <div className="text-start mt-4">
                          <label className="mb-1 fw-medium">
                            <small>Relation</small>
                          </label>
                          <br />
                          <select
                            name={`emergencyContactInformation[${index}].authorizedRelation`}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className="form-select"
                            value={formik.values.emergencyContactInformation[index].authorizedRelation || ""}
                          >
                            <option value="">Select Relation</option>
                            <option value="Father">Father</option>
                            <option value="Mother">Mother</option>
                            <option value="Grandparents">Grandparents</option>
                            <option value="Helper">Helper</option>
                            <option value="Others">Others</option>
                          </select>
                          {formik.touched.emergencyContactInformation?.[index]?.authorizedRelation &&
                            formik.errors.emergencyContactInformation?.[index]?.authorizedRelation && (
                              <div className="text-danger">
                                <small>
                                  {formik.errors.emergencyContactInformation[index].authorizedRelation}
                                </small>
                              </div>
                            )}
                        </div>
                        <div className="text-start mt-4">
                          <label className="mb-1 fw-medium">
                            <small>Emergency Contact Address</small>
                          </label>
                          <br />
                          <textarea
                            className="form-control"
                            rows={5}
                            name={`emergencyContactInformation[${index}].emergencyContactAddress`}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.emergencyContactInformation[index].emergencyContactAddress || ""}
                          />
                          {formik.touched.emergencyContactInformation?.[index]?.emergencyContactAddress &&
                            formik.errors.emergencyContactInformation?.[index]?.emergencyContactAddress && (
                              <div className="text-danger">
                                <small>
                                  {formik.errors.emergencyContactInformation[index].emergencyContactAddress}
                                </small>
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-6 col-12 px-5">
                        <div className="text-start mt-4">
                          <label className="mb-1 fw-medium">
                            <small>Contact No</small>
                          </label>
                          <br />
                          <PhoneInput
                            country={"sg"}
                            enableSearch={true}
                            inputProps={{
                              name: `emergencyContactInformation[${index}].contactNo`,
                              onBlur: formik.handleBlur,
                            }}
                            value={formik.values.emergencyContactInformation[index].contactNo || ""}
                            onChange={(phone) =>
                              formik.setFieldValue(`emergencyContactInformation[${index}].contactNo`, "+" + phone)
                            }
                            containerClass="phone-input-container"
                            inputClass="phone-input"
                            buttonClass="phone-flag-button"
                          />
                          {formik.touched.emergencyContactInformation?.[index]?.contactNo &&
                            formik.errors.emergencyContactInformation?.[index]?.contactNo && (
                              <div className="text-danger">
                                <small>
                                  {formik.errors.emergencyContactInformation[index].contactNo}
                                </small>
                              </div>
                            )}
                        </div>
                        <div className="text-start mt-4">
                          <label className="mb-1 fw-medium">
                            <small>Postal Code</small>
                          </label>
                          <br />
                          <input
                            className="form-control"
                            type="text"
                            name={`emergencyContactInformation[${index}].postalCode`}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.emergencyContactInformation[index].postalCode || ""}
                          />
                          {formik.touched.emergencyContactInformation?.[index]?.postalCode &&
                            formik.errors.emergencyContactInformation?.[index]?.postalCode && (
                              <div className="text-danger">
                                <small>
                                  {formik.errors.emergencyContactInformation[index].postalCode}
                                </small>
                              </div>
                            )}
                        </div>
                        <div className="text-start mt-4">
                          <label htmlFor="" className="fw-medium">
                            <small>Person Profile</small>
                          </label>
                          <br />
                          <input
                            type="file"
                            name={`emergencyContactInformation[${index}].files`}
                            className="form-control"
                            onChange={(event) => {
                              const file = event.target.files[0];
                              formik.setFieldValue(`emergencyContactInformation[${index}].files`, file);
                              const reader = new FileReader();
                              reader.onload = (e) => {
                                setProfileImage((prev) => {
                                  const newImages = [...prev];
                                  newImages[index] = e.target.result;
                                  return newImages;
                                });
                              };
                              if (file) reader.readAsDataURL(file);
                            }}
                            onBlur={formik.handleBlur}
                            accept=".jpg, .jpeg, .png, .gif, .bmp"
                          />
                          {formik.touched.emergencyContactInformation?.[index]?.files &&
                            formik.errors.emergencyContactInformation?.[index]?.files && (
                              <div className="text-danger">
                                <small>
                                  {formik.errors.emergencyContactInformation[index].files}
                                </small>
                              </div>
                            )}
                          {profileImage[index] && (
                            <img
                              src={profileImage[index]}
                              alt="Uploaded Preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                                marginTop: "10px",
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {formik.values.emergencyContactInformation.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => {
                        if (row.id) {
                          handleDeleteRow(index);
                        } else {
                          handleRemoveRow(index);
                        }
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="row">
            <div className="col-12 mb-4">
              <button
                type="button"
                className="btn btn-sm text-white"
                style={{
                  fontWeight: "600px !important",
                  background: "#eb862a",
                }}
                onClick={() => {
                  const newEmergencyContactInfo = {
                    name: "",
                    authorizedRelation: "",
                    contactNo: "",
                    postalCode: "",
                    emergencyContactAddress: "",
                    files: null,
                  };
                  formik.setFieldValue("emergencyContactInformation", [
                    ...formik.values.emergencyContactInformation,
                    newEmergencyContactInfo,
                  ]);
                  setRows((prev) => [...prev, {}]);
                  setProfileImage((prev) => [...prev, null]);
                }}
              >
                Add More
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }
);

export default AddEmergencyContact;